import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { ABORT_REASONS, MAX_CIRCLE_BACKS } from '@nu/shared';
import PhotoPicker from '../../components/PhotoPicker.jsx';
import { abortJob, circleBackJob } from './progress.js';

// Pop-up for a job the team is on but can't install: why (Other needs
// words), optional photos, then either come back later today (circle back:
// only for reasons where that could help, and once per job) or abort it.
// `start` prefills it (e.g. after an incident that stops the job).
export default function AbortJob({ day, job, onClose, start = {} }) {
  const ref = useRef(null);
  const [reason, setReason] = useState(start.reason || '');
  const [note, setNote] = useState(start.note || '');
  const [photos, setPhotos] = useState([]);
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    ref.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const picked = ABORT_REASONS.find((r) => r.value === reason);
  const triedAgain = (job.circleBacks || 0) >= MAX_CIRCLE_BACKS;
  const canCircle = picked?.circleBack && !triedAgain;
  const ready = picked && (reason !== 'Other' || note.trim()) && !adding;
  const why = reason === 'Other' ? note.trim() : null;
  const blobs = () => photos.map((p) => p.blob);

  return (
    <div className="popup-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="popup" role="dialog" aria-modal="true" aria-labelledby="abort-title" tabIndex={-1} ref={ref}>
        <button type="button" className="popup-x" aria-label="Close" onClick={onClose}><X size={20} aria-hidden="true" /></button>
        <p className="popup-kicker">{job.reference} · Can&apos;t install</p>
        <h2 id="abort-title" className="popup-title">{[job.addressLine1, job.postcode].filter(Boolean).join(', ')}</h2>
        <p className="popup-note">Why can&apos;t you install?</p>
        <div className="reason-chips" role="radiogroup" aria-label="Why?">
          {ABORT_REASONS.map((r) => (
            <button key={r.value} type="button" role="radio" aria-checked={reason === r.value} className={`chip${reason === r.value ? ' on' : ''}`} onClick={() => setReason(r.value)}>{r.value}</button>
          ))}
        </div>
        {reason === 'Other' && (
          <label className="popup-field">
            <span>Reason for Other <span className="req">(required)</span></span>
            <input className="popup-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Water main leaking at the stop tap" maxLength={200} required aria-required="true" autoFocus />
            {!note.trim() && <span className="popup-hint">Type a reason to continue.</span>}
          </label>
        )}

        <PhotoPicker photos={photos} setPhotos={setPhotos} onBusy={setAdding} stamp={job.reference} />

        {picked && triedAgain && picked.circleBack && <p className="small muted">Already tried again once today, so it can only be aborted.</p>}
        {canCircle && (
          <>
            <button type="button" className="btn primary popup-btn" disabled={!ready} onClick={() => { circleBackJob(day, job, reason, why, blobs()); onClose(); }}>Come back later today</button>
            <p className="small muted popup-sub">Goes to the end of today&apos;s jobs. You carry on with the next one.</p>
          </>
        )}
        <button type="button" className="btn danger-btn popup-btn" disabled={!ready} onClick={() => { abortJob(job, reason, why, blobs()); onClose(); }}>Abort job</button>
        <button type="button" className="btn popup-btn popup-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
